import { useDisclosure } from '@chakra-ui/react';
import { IContract } from '@monetize/types/app';
import {
  FilterOptionType,
  FilterType,
  FilterTypeOperator,
  RenderTableFilterOptionProps,
} from '@monetize/types/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useGetContracts } from '../../api/cpqService';
import {
  MFlex,
  MHStack,
  MPageContentContainer,
  MPageHeader,
  MPageLoader,
} from '../../components/Monetize';
import {
  AccountIdTableFilterOptionContent,
  AmountTableFilterOptionContent,
  DateGlteTableFilterOptionContent,
  MDataTableFilter,
} from '../../components/Monetize/DataTable';
import { BillGroupIdTableFilterOptionContent } from '../../components/Monetize/DataTable/FilterOptions/BillGroupIdTableFilterContent';
import { CurrencyTableFilterOptionContent } from '../../components/Monetize/DataTable/FilterOptions/CurrencyTableFilterOptionContent';
import {
  buildHideTestAccountFilterOption,
  TEST_ACCOUNT_FILTER_KEY,
} from '../../components/Monetize/DataTable/FilterOptions/HideTestAccountFilterOption';
import { OwnerTableFilterOptionContent } from '../../components/Monetize/DataTable/FilterOptions/OwnerTableFilterOptionContent';
import { TeamTableFilterOptionContent } from '../../components/Monetize/DataTable/FilterOptions/TeamTableFilterOptionContent';
import { useTableColumns } from '../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ExternalFilterDef,
  useExternalFilters,
} from '../../components/Monetize/DataTableNew/useExternalFilters';
import { ExportEntityButton } from '../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../components/Monetize/RefreshTableButton';
import { RenewalModal } from '../../components/RenewalModal/RenewalModal';
import { CONTRACTS, ROUTES } from '../../constants';
import { getSalesContractEditRoute } from '../../constants/routes';
import { IContractWithBillGroup } from '../../hooks/AccountDetail';
import { useDocumentHead } from '../../services/documentHead';
import {
  tableFilterSelector,
  tablePagerSelector,
} from '../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
} from '../../utils';
import { navigateOrOpenNewWindow } from '../../utils/misc';
import { getFiltersFromState } from '../../utils/tableUtils';
import ContractActions from '../Quotes/Quote/ContractActions';
import {
  CONTRACT_LIST_CATALOG,
  ContractListColumnContext,
} from './contractList.columns';
const ACCOUNT_FILTER_OPTION = {
  title: 'Account',
  key: 'accountId',
  operator: FilterTypeOperator.EQUAL,
  renderOptionContent: (props: RenderTableFilterOptionProps) => (
    <AccountIdTableFilterOptionContent {...props} />
  ),
};

const AMOUNT_FILTER_OPTION = {
  title: 'Amount',
  key: 'totalValue',
  operator: FilterTypeOperator.GLTE,
  renderOptionContent: (props: RenderTableFilterOptionProps) => (
    <AmountTableFilterOptionContent {...props} />
  ),
};

const OWNER_FILTER_OPTION = {
  title: 'Owner',
  key: 'owner',
  operator: FilterTypeOperator.EQUAL,
  renderOptionContent: OwnerTableFilterOptionContent,
};

const CURRENCY_FILTER_OPTION = {
  title: 'Currency',
  key: 'currency',
  operator: FilterTypeOperator.EQUAL,
  renderOptionContent: (optionsProps: RenderTableFilterOptionProps) => (
    <CurrencyTableFilterOptionContent {...optionsProps} />
  ),
};

const BILL_GROUP_ID_FILTER_OPTION = {
  title: 'Bill Group',
  key: 'billGroupId',
  operator: FilterTypeOperator.EQUAL,
  renderOptionContent: (props: RenderTableFilterOptionProps) => (
    <BillGroupIdTableFilterOptionContent {...props} />
  ),
};

const START_DATE_FILTER_OPTION = {
  title: 'Start Date',
  key: 'startDate',
  operator: FilterTypeOperator.GLTE,
  renderOptionContent: (optionsProps: RenderTableFilterOptionProps) => (
    <DateGlteTableFilterOptionContent {...optionsProps} />
  ),
};

const TEAM_FILTER_OPTION = {
  title: 'Team',
  key: 'teamId',
  operator: FilterTypeOperator.EQUAL,
  renderOptionContent: TeamTableFilterOptionContent,
};

export const CONTRACT_FILTER_OPTIONS = [
  ACCOUNT_FILTER_OPTION,
  AMOUNT_FILTER_OPTION,
  CURRENCY_FILTER_OPTION,
  CONTRACTS.CONTRACT_END_ACTION_FILTER_OPTION,
  OWNER_FILTER_OPTION,
  START_DATE_FILTER_OPTION,
  CONTRACTS.CONTRACT_STATUS_FILTER_OPTION,
  TEAM_FILTER_OPTION,
];

export const CONTRACT_FILTER_OPTIONS_FOR_ACCOUNT_DETAIL = [
  AMOUNT_FILTER_OPTION,
  CONTRACTS.CONTRACT_END_ACTION_FILTER_OPTION,
  CONTRACTS.CONTRACT_STATUS_FILTER_OPTION,
];

const CONTRACT_TOGGLE_FILTER_OPTIONS: FilterOptionType[] = [
  buildHideTestAccountFilterOption('Hide Test Account Contracts'),
];

const CONTRACT_LIST_FILTER_DEFS: ExternalFilterDef[] = [
  { key: TEST_ACCOUNT_FILTER_KEY, columnHeader: 'Hide Test Account' },
];

type FilterKey =
  | FilterStateKeys.CONTRACT_LIST
  | FilterStateKeys.CONTRACT_DASHBOARD_LIST;

export const useContractsTableData = (
  filterKey: FilterKey = FilterStateKeys.CONTRACT_LIST,
) => {
  const navigate = useNavigate();
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(filterKey),
  );
  const { state: locationState } = useLocation();

  const [pager, setPager] = useAtom(tablePagerSelector(filterKey));
  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('description');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(filters, searchTerm, searchKey),
    [filters, searchTerm, searchKey],
  );

  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Contracts');
  }, []);

  useEffect(() => {
    setFilters(
      getFiltersFromState(
        (locationState || {}) as Record<string, any>,
        filters,
        CONTRACT_FILTER_OPTIONS,
      ),
    );
  }, []);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const [selectedContract, setSelectedContract] =
    useState<IContractWithBillGroup | null>(null);

  const {
    open: isOpenRenewalModal,
    onOpen: onOpenRenewalModal,
    onClose: onCloseRenewalModal,
  } = useDisclosure();

  const {
    isLoading,
    isRefetching,
    isFetched,
    isError,
    data: contractList,
    error,
    refetch,
  } = useGetContracts(
    {
      config: pager,
      filters: filterParams,
    },
    {
      placeholderData: (previousData) => previousData,
      meta: { showErrorToast: true },
    },
  );

  useEffect(() => {
    if (isError) {
      setPager(getDefaultPager(filterKey));
      setFilters(getDefaultFilter(filterKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, isError]);

  const handleEarlyRenewal = useCallback(
    (data: IContractWithBillGroup) => {
      setSelectedContract(data);
      onOpenRenewalModal();
    },
    [onOpenRenewalModal],
  );

  const actionBodyTemplate = useCallback(
    (data: IContract) => (
      <ContractActions
        contract={data}
        handleEarlyRenewal={handleEarlyRenewal}
      />
    ),
    [handleEarlyRenewal],
  );

  const onResetFilter = () => {
    setFilters([]);
    setPager(getDefaultPager(filterKey));
  };

  const handleEditContract = (contractId: string, openInNewWindow = false) =>
    navigateOrOpenNewWindow(
      navigate,
      getSalesContractEditRoute(contractId),
      openInNewWindow,
    );

  const columnContext = useMemo<ContractListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    CONTRACT_LIST_CATALOG,
    columnContext,
  );

  return {
    contractList,
    isLoading,
    isRefetching,
    isFetched,
    searchKey,
    searchTerm,
    setSearchTerm,
    pager,
    setPager,
    actionBodyTemplate,
    handleEditContract,
    filters,
    setFilters,
    onResetFilter,
    columns,
    columnState,
    selectedContract,
    isOpenRenewalModal,
    onCloseRenewalModal,
    isError,
    error,
    refetch,
    navigate,
  };
};

export const ContractListMain = () => {
  const {
    contractList,
    isLoading,
    isRefetching,
    isFetched,
    searchTerm,
    pager,
    setPager,
    filters,
    setFilters,
    onResetFilter,
    handleEditContract,
    columns,
    columnState,
    selectedContract,
    isOpenRenewalModal,
    onCloseRenewalModal,
    refetch,
  } = useContractsTableData();

  const tableRef = useRef<DataTableRef>(null);
  const filterComponentReset = useRef<any>(null);

  const {
    initialFilters,
    onFiltersChange,
    extraActiveFilters,
    externalFilters,
    setExternalFilters,
  } = useExternalFilters({
    filters,
    setFilters,
    pager,
    setPager,
    filterDefs: CONTRACT_LIST_FILTER_DEFS,
  });

  const onResetFilters = useCallback(() => {
    onResetFilter();
    tableRef.current?.resetFilters();
  }, [onResetFilter]);

  if (isLoading || !contractList) {
    return <MPageLoader />;
  }

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading: isLoading,
    totalElements: contractList.totalElements,
    filters,
    searchTerm,
    page: pager.page,
  });

  return (
    <MPageContentContainer>
      <MPageHeader size="md" title="Contracts">
        {!isTrulyEmptyList && (
          <MFlex>
            <MHStack gap="2">
              <RefreshTableButton refetch={refetch} />
              <MDataTableFilter
                filters={externalFilters}
                filterOptions={CONTRACT_TOGGLE_FILTER_OPTIONS}
                setFilters={setExternalFilters}
                onResetFilter={onResetFilters}
                resetFilter={filterComponentReset}
              />
              <ExportEntityButton
                entity="contracts"
                filters={filters}
                sortField={pager.sortField}
                sortOrder={pager.sortOrder}
              />
            </MHStack>
          </MFlex>
        )}
      </MPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={contractList.content || []}
        isLoading={isLoading || (isRefetching && !isFetched)}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={contractList.totalElements}
        totalPages={contractList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={initialFilters}
        initialSortingState={[{ id: 'startDate', desc: true }]}
        onFiltersChange={onFiltersChange}
        extraActiveFilters={extraActiveFilters}
        onRowClick={(row, _tableRow, openInNewWindow) =>
          handleEditContract(row.id, openInNewWindow)
        }
        emptyProps={{
          mainMessage: 'Looks like there are no contracts here.',
          btnLabel: 'View Quotes',
          to: ROUTES.SALES_QUOTES_ROUTE,
        }}
        emptyFilterResultsProps={{ btnLabel: 'Reset Filters' }}
        resetFilter={onResetFilters}
      />

      {selectedContract && isOpenRenewalModal && (
        <RenewalModal
          contract={selectedContract}
          onClose={onCloseRenewalModal}
        />
      )}
      <Outlet />
    </MPageContentContainer>
  );
};
